import React, {
	JSXElementConstructor,
	ReactElement,
	useEffect,
	useRef,
	useState,
} from "react";
import {
	useTable,
	usePagination,
	TableInstance,
	UsePaginationInstanceProps,
	useBlockLayout,
} from "react-table";
import { AnimatePresence, motion } from "framer-motion";
import { useSticky } from "react-table-sticky";
import { tableVariants } from "../../../../motionVariants";
import { useLocation, useNavigate } from "react-router-dom";

////////  HOOK IMPORTS ////////
import {
	useConstructUrl,
	useDarkMode,
	useFetchData,
	useHandleClear,
	useHandlePopState,
	useHostName,
	usePopState,
	useUrl,
	useDataTableState,
	useDataTableContext,
	usePageDataContext,
	useHandleRowClick,
	useUrlSync,
	useManageModalUrl,
} from "../../../../hooks";
////////  TABLE IMPORTS ////////
import convertDataUtil, {
	generateDynamicConfig,
} from "./helperFunctions/convertData";
////////  TYPE IMPORTS ////////
import { CustomTableState, PrimaryTableProps } from "../types";
import { ModalType } from "../../../../pages/Orders/view/ApprovalView/types";

////////  COMPONENT IMPORTS ////////
import {
	LoadingMessage,
	Toaster,
	TablePagination,
	ActionList,
	TableContent,
} from "../../..";

/////  HELPER FUNCTION IMPORTS ////////
import { renderModalContent } from "./helperFunctions/renderModalContent";
import { updateRowData } from "./helperFunctions/updateRowData";
import { renderApprovalActionsModal } from "./helperFunctions/renderApprovalActionsModal";
import { quickActionListData } from "./helperFunctions/quickActionListData";
import {
	formatTableData,
	generateSupplyAccessors,
} from "./helperFunctions/formatTableData";
import {
	flattenArray,
	replaceStatusWithNames,
	updateUrlWithNewPage,
} from "../../../../utils";

export type StatusReplacement = {
	slug: string;
	name: string;
	colorHex: string;
};

const PrimaryDataTable: React.FC<PrimaryTableProps> = ({
	setHasModalTable,
	additionalData,
	callFromFilter,
	setCallFromFilter,
	slug: propSlug,
	callClickAction,
	tableRefresh,
	setIsTableLoading,
	skipNavigateToPreviousUrl,
	viewSlug,
	selectedPurchaseOrderItemUuid,
	subtableSlug,
	subtableUuid,
	isTableSearchAndSortEnabled,
	hasTableRowExpansion,
	tableDisplayData,
	quickApprovalDecisionListFields,
}) => {
	const { salesOrderStageId } = additionalData;
	const { darkMode } = useDarkMode();
	const {
		activeIndex,
		setActiveIndex,
		currentPage,
		setCurrentPage,
		isLoading,
		setIsLoading,
		setNoDataFound,
		isError,
		setIsError,
		data,
		setData,
		convertedData,
		setConvertedData,
		sortColumn,
		setSortColumn,
		resetSearch,
		setResetSearch,
		editingHeader,
		setEditingHeader,
		setHeaderValue,
		searchText,
		setSearchText,
		submittedSearchText,
		setSubmittedSearchText,
		detailsAreUndefined,
		setDetailsAreUndefined,
		showDetailsBox,
		setShowDetailsBox,
		columnTitles,
		setColumnTitles,
		totalPages,
		setTotalPages,
		columnMappings,
		setColumnMappings,
		searchColumn,
		setSearchColumn,
		clearSearch,
		prevPageFromURL,
		setPrevPageFromURL,
		recordsPerPageAfterChange,
		setRecordsPerPageAfterChange,
		paginationInput,
		setItemUuid,
		dataTableValueFields,
		setDataTableValueFields,
		setTotalRecords,
		totalRecords,
	} = useDataTableState();
	const slug = propSlug || "";

	const [activeModal, setActiveModal] = useState<ModalType>(null);

	const location = useLocation();

	const isPathnameInventory_Items = location.pathname === "/inventory_items";

	const navigate = useNavigate();
	const {
		activeUuid,
		setActiveUuid,
		setActiveRowUuid,
		activeRowUuid,
		deactivatedQuickMenuIds,
		setDeactivatedQuickMenuIds,
		toggleStates,
		displayStickyColumn,
		setInfiniteScrollIsLoadingMore,
		setTotalRecordsFromTable,
		isNestedChildTable,
		setIsNestedTable,
		isNestedChildTableModalToggle,
		setLeftLockedColumns,
		isModalOpen,
		setIsModalOpen,
		toggleModal,
		closeModal,
		setSearchColumn: setSearchColumnFromContext,
		searchCriteria,
		hasInfiniteScroll,
		expandedCells,
		hasCustomCellToggle,
	} = useDataTableContext();

	const hasExpandedCells = Object.keys(expandedCells || {}).length > 0;

	const [nextPage, setNextPage] = useState<number>(2);
	const isFetchingRef = useRef(false);
	const loadNextPage = async () => {
		if (!hasInfiniteScroll) return;
		if (isFetchingRef.current) return; //

		isFetchingRef.current = true;

		setInfiniteScrollIsLoadingMore?.(true);
		await fetchData({ page: nextPage, append: true });
		setNextPage((p) => p + 1);
		setInfiniteScrollIsLoadingMore?.(false);

		isFetchingRef.current = false;
	};

	const { previousUrl, setPreviousUrl } = useUrl();
	const [visibleListId, setVisibleListId] = useState<string | null>(null);

	const [rowData, setRowData] = useState<any[]>([]);
	const [isRowDataInitialized, setIsRowDataInitialized] = useState(false);
	const { hostName } = useHostName();
	const rowsPerPage = [10, 15, 20, 25, 50, 100];
	const keysArray =
		rowData.length > 0 ? Object.keys(rowData[0]?.data || {}) : [];
	const handleUpdateRowData = (uuid: string, key: string, newValue: any) => {
		updateRowData({
			uuid,
			key,
			newValue,
			setRowData,
			keysArray,
		});
	};

	const { pageMeta, pageTitle } = usePageDataContext();
	const [typeValues, setTypeValues] = useState<Record<string, any>>();
	const queryParams = new URLSearchParams(location.search);
	let nestedTableUuid: string | null = null;
	let nestedPrimaryRecordSlug: string | null = null;

	const urlKeys = JSON.parse(localStorage.getItem("urlKeys") ?? "[]");
	const isPathnameInventoryItems =
		window.location.pathname === "/inventory_items";
	if (urlKeys && urlKeys?.length > 0 && !isPathnameInventoryItems) {
		for (const paramKey of urlKeys) {
			const nestedParam = queryParams.get(paramKey?.key);
			if (nestedParam) {
				nestedTableUuid = nestedParam;
				nestedPrimaryRecordSlug = paramKey.nestedPrimaryRecordSlug;
			}
		}
	}
	//Set the rowData from the data that comes in from the API call so mutable data can be used for the table live updates
	useEffect(() => {
		if (data && data.length > 0 && !isRowDataInitialized) {
			setRowData(data);
			setIsRowDataInitialized(true);
		}
		setTotalRecordsFromTable && setTotalRecordsFromTable(totalRecords);
	}, [data, isRowDataInitialized]);

	useManageModalUrl(
		pageTitle,
		activeUuid,
		setActiveUuid,
		isModalOpen,
		setIsModalOpen,
		setCallFromFilter,
		callFromFilter,
	);

	const { handleClear } = useHandleClear(
		setDetailsAreUndefined,
		setNoDataFound,
		setSearchText,
		setSubmittedSearchText,
		setSortColumn,
		setResetSearch,
		setCurrentPage,
		resetSearch,
	);

	function convertColumnTitles(pageMeta: any, dataTableValueFields: any) {
		return dataTableValueFields.map((field: any) => {
			const table = field?.settings?.table;
			const fieldName = field?.settings?.field;
			const tableViewField = field?.tableViewField;

			const slug = field?.slug;
			let labelSingular;
			if (field?.settings?.contextField !== null) {
				let contextTable = field?.settings?.contextTable;
				let contextField = field?.settings?.contextField;
				labelSingular =
					pageMeta?.settings?.fields?.[table]?.[fieldName]?.[
						contextTable
					]?.[contextField]?.["label-singular"] || "";
			} else {
				labelSingular =
					pageMeta?.settings?.fields?.[table]?.[fieldName]?.[
						"label-singular"
					] || "";

				if (!labelSingular && typeof tableViewField === "string") {
					const parts = tableViewField.split(".");
					if (parts.length === 2) {
						const [maybeTable, maybeField] = parts;
						labelSingular =
							pageMeta?.settings?.fields?.[maybeTable]?.[
								maybeField
							]?.["label-singular"] || "";
					}
				}
			}

			if (!labelSingular) {
				labelSingular = slug || fieldName || "No Label Found";
			}

			return {
				labelSingular,
			};
		});
	}

	const convertedColumnTitles = flattenArray(
		convertColumnTitles(pageMeta, dataTableValueFields),
	);
	const approvalActionModal = renderApprovalActionsModal({
		activeModal,
		setActiveModal,
		activeRowUuid: activeRowUuid || "",
		activeUuid: activeUuid || "",
		handleUpdateRowData,
		setDeactivatedQuickMenuIds,
	});

	const actionList = quickApprovalDecisionListFields && (
		<>
			<ActionList
				containerId={"portal-root"}
				activeRowUuid={activeRowUuid || ""}
				setQuickMenuVisible={setVisibleListId}
				fields={quickApprovalDecisionListFields}
				setActiveModal={setActiveModal}
			/>
		</>
	);

	const handleIconClick = (id: string, rowUuid: string) => {
		setActiveRowUuid(rowUuid);
		setActiveUuid(rowUuid);
		setVisibleListId((prevId) => (prevId === id ? null : id));
	};

	const accessors = generateSupplyAccessors(convertedColumnTitles);
	const columns = React.useMemo(() => {
		const dynamicConfig = generateDynamicConfig(
			dataTableValueFields as any,
		);

		return formatTableData({
			columnTitles: convertedColumnTitles,
			accessors,
			columnConfigs: dynamicConfig.default || [],
			typeValues: Array.isArray(typeValues) ? typeValues : [],
			showFilterSlugToggle: callFromFilter,
			visibleListId,
			handleIconClick,
			actionList,
			tableData: rowData,
			deactivatedMenuUuids: deactivatedQuickMenuIds,
			showStickyColumn: displayStickyColumn,
			leftLockedColumnCount: tableDisplayData?.leftLockedColumns || -1,
			suffixColumnIndexes: tableDisplayData?.suffixColumnIndexes || [],
			cellClickAction: callClickAction,
			isNestedChildTable,
			isNestedTable: tableDisplayData?.isNestedTable,
			activeRowUuid: activeRowUuid || "",
			hasExpandedCells,
			hasCustomCellToggle: hasCustomCellToggle,
			tableDisplayData,
		});
	}, [
		rowData,
		columnTitles,
		dataTableValueFields,
		callFromFilter,
		visibleListId,
		displayStickyColumn,
		deactivatedQuickMenuIds,
		isNestedChildTable,
		activeRowUuid,
		hasExpandedCells,
		hasCustomCellToggle,
	]);

	useEffect(() => {
		if (setIsTableLoading) {
			setIsTableLoading(isLoading);
		}
	}, [isLoading, setIsTableLoading]);

	useEffect(() => {
		const dynamicConfig = generateDynamicConfig(
			dataTableValueFields as any,
		);

		// Ensure `replaceStatusWithNames` is called with correct data
		const updatedProcessedData = replaceStatusWithNames(
			rowData,
			Array.isArray(typeValues) ? typeValues : [],
		);

		// Set converted data with the processed data
		setConvertedData(
			convertDataUtil(updatedProcessedData as any, dynamicConfig),
		);
	}, [rowData, dataTableValueFields, typeValues]);

	useEffect(() => {
		const dynamicConfig = generateDynamicConfig(
			dataTableValueFields as any,
		);

		// Process and convert data within this effect
		const updatedProcessedData = replaceStatusWithNames(
			data,
			Array.isArray(typeValues) ? typeValues : [],
		);

		// Set converted data with the processed data
		setConvertedData(
			convertDataUtil(updatedProcessedData as any, dynamicConfig),
		);
		if (data && data.length > 0) {
			setRowData(data);
		}
	}, [data, dataTableValueFields, typeValues]);

	const defaultColumn = React.useMemo(
		() =>
			hasInfiniteScroll || isNestedChildTable
				? { minWidth: 200, width: 300 }
				: undefined,
		[hasInfiniteScroll],
	);

	const plugins = React.useMemo(() => {
		const list: any[] = [];

		if (hasInfiniteScroll) list.push(useBlockLayout);
		list.push(useSticky, usePagination);
		return list;
	}, [hasInfiniteScroll]);

	const instance = useTable<Record<string, string | React.ReactElement>>(
		{
			columns,
			data: convertedData,
			defaultColumn,
			initialState: {
				pageIndex: 0,
				pageSize: recordsPerPageAfterChange,
			} as any,
		},
		...plugins,
	) as TableInstance<Record<string, string | React.ReactElement>> &
		UsePaginationInstanceProps<Record<string, string | React.ReactElement>>;

	const {
		getTableProps,
		getTableBodyProps,
		headerGroups,
		page,
		state,
		setPageSize,
		prepareRow,
		rows,
	} = instance;

	const { pageSize } = state as CustomTableState<{
		[key: string]:
			| string
			| ReactElement<any, string | JSXElementConstructor<any>>;
	}>;

	const fetchData = useFetchData({
		slug,
		setIsLoading,
		setColumnMappings,
		setData,
		setColumnTitles,
		setTotalPages,
		setIsError,
		setNoDataFound,
		nestedPrimaryRecordSlug,
		nestedTableUuid,
		setDataTableValueFields,
		setTypeValues,
		additionalData,
		setTotalRecords,
		pageTitle,
		selectedPurchaseOrderItemUuid,
		subtableSlug,
		subtableUuid,
	});

	const handlePopState = useHandlePopState(
		prevPageFromURL,
		setPrevPageFromURL,
		fetchData,
	);

	const constructedUrl = useConstructUrl({
		slug,
		searchCriteria,
		pageSize,
		sortColumn,
		columnMappings,
		page: currentPage,
		pageMeta,
	});

	// Using the usePopState custom hook to handle popstate events
	usePopState(handlePopState);
	//Initializes page title and fetches initial data on component mount, Sets up popstate event listeners for navigation, and cleans up on unmount
	useEffect(() => {
		if (hostName === "") return;
		setHasModalTable && setHasModalTable(true);
		fetchData();

		setVisibleListId(null);
		setSearchColumnFromContext(searchColumn?.Header || "");
		setLeftLockedColumns(tableDisplayData?.leftLockedColumns || 0);
		setIsNestedTable(tableDisplayData?.isNestedTable || false);
	}, [
		pageSize,
		slug,
		currentPage,
		sortColumn,
		submittedSearchText,
		hostName,
		clearSearch,
		resetSearch,
		paginationInput,
		callFromFilter,
		salesOrderStageId,
		searchCriteria,
		tableRefresh,
	]);
	const prevValuesRef = useRef<any>(null);

	const hasMountedRef = useRef(false);

	useEffect(() => {
		const currentValues = {
			searchCriteria,
			clearSearch,
			resetSearch,
			paginationInput,
			sortColumn,
			pageSize,
			currentPage,
		};

		if (!hasMountedRef.current) {
			hasMountedRef.current = true;
			prevValuesRef.current = currentValues;
			return;
		}

		const prev = prevValuesRef.current;

		if (JSON.stringify(prev) !== JSON.stringify(currentValues)) {
			sessionStorage.removeItem("table_first_mount");
			prevValuesRef.current = currentValues;
		}
	}, [
		searchCriteria,
		clearSearch,
		resetSearch,
		paginationInput,
		sortColumn,
		pageSize,
		currentPage,
	]);

	useEffect(() => {
		// Ensure `where` clause or any additional data is in `constructedUrl`
		if (constructedUrl && constructedUrl.includes("where")) {
			fetchData();
		}
	}, [constructedUrl]);

	// Updates sortColumn without adding invalid keys (e.g., col)
	const updateSortColumn = (key: string, direction: string) => {
		setSortColumn((prev: any) => {
			// Clone the existing sortColumn object
			const updatedSortColumn = { ...prev };
			// Update or add the key with its direction
			updatedSortColumn[key] = `${direction}${key}`;
			// Return the updated sortColumn object
			return updatedSortColumn;
		});
	};
	// Handles sort changes from headers
	const handleHeaderSortClick = (columnKey: string) => {
		const currentDirection =
			sortColumn[columnKey]?.[0]?.[0] === "+" ? "-" : "+";
		updateSortColumn(columnKey, currentDirection);
	};
	//Updates URL parameters and synchronizes state with URL changes, including sorting, searching, and pagination

	useEffect(() => {
		const isFirstMount =
			sessionStorage.getItem("table_first_mount") === "true";
		if (isFirstMount) return;

		setPageSize(
			hasInfiniteScroll ? 101 : Number(recordsPerPageAfterChange),
		);
	}, [recordsPerPageAfterChange, setPageSize, hasInfiniteScroll]);

	useEffect(() => {
		const p = new URLSearchParams(location.search).get(`${slug}_page`);
		if (p) {
			setCurrentPage(Number(p));
		}
	}, []);

	const setCurrentPageAndUpdateUrl = (newPage: number) => {
		setCurrentPage(newPage);
		const params = new URLSearchParams(location.search);
		params.set(`${slug}_page`, String(newPage));
		navigate(`?${params.toString()}`, { replace: true });
	};

	const handleRowClick = useHandleRowClick(
		data,
		setActiveIndex,
		setShowDetailsBox,
		setItemUuid,
	);

	//used to correct sync issue with fulfillments modal and uuid
	useEffect(() => {
		let uuidFromLocalStorage = localStorage.getItem("modalUuid");

		if (
			!slug ||
			typeof uuidFromLocalStorage !== "string" ||
			!uuidFromLocalStorage.trim()
		)
			return;

		fetchData();
	}, [slug]);

	useUrlSync({
		previousUrl,
		setPreviousUrl,
		constructedUrl,
		currentPage,
		setCurrentPage,
		setSearchText,
		submittedSearchText,
		sortColumn,
		pageSize,
		updateUrlWithNewPage,
		slug,
		setRecordsPerPageAfterChange,
		skipNavigateToPreviousUrl,
	});

	if (isLoading) {
		return (
			<div
				className={`${
					isNestedChildTable
						? "min-h-96 max-h-96"
						: "min-h-[750px] max-h-[750px]"
				} flex items-center justify-center `}
			>
				<LoadingMessage
					message=""
					containerClasses="items-center justify-center mt-32 "
				/>
			</div>
		);
	}

	if (isError) {
		return (
			<Toaster
				message="Something went wrong"
				color="red-500"
				additionalClasses="flex justify-center"
			/>
		);
	}

	if (detailsAreUndefined) {
		return (
			<Toaster
				clearMessage={handleClear}
				message="Search returned no results"
				color="red-500"
			/>
		);
	}

	//activeRow is used to determine if the clicked row's status is "pendingInitialApproval"
	const activeRowStatus = rowData.find((row) => row.data.uuid === activeUuid);
	//decides what modal to render
	const modal = renderModalContent({
		pageTitle,
		isModalOpen,
		closeModal,
		data,
		hostName,
		activeRowStatus,
		updateRowData: handleUpdateRowData,
		isPathnameInventory_Items,
	});

	// const shouldShowModal = !tableDisplayData?.isNestedTable
	// 	? isModalOpen
	// 	: isNestedChildTable

	let shouldShowModal = isModalOpen;

	return (
		<AnimatePresence>
			<motion.div
				variants={tableVariants}
				initial="hidden"
				animate="visible"
				exit="exit"
				className={` ${
					isPathnameInventory_Items ? "" : "h-[calc(100%-82px)]"
				} `}
			>
				<div
					id="copyable-cell-portal"
					className="absolute inset-0 pointer-events-none z-50"
				/>
				<div
					className={`parent-container  h-full ${
						darkMode ? "text-darkText" : "text-black"
					}`}
				>
					<div
						className={` ${
							isPathnameInventory_Items
								? ""
								: "h-[calc(100%-72px)]"
						} `}
					>
						<div className={`min-w-full h-full`}>
							<div
								className={`${
									isNestedChildTable
										? ""
										: `border-t-1 border-l-1 border-r-1  border-navy-250`
								} ut-wrapper max-w-full min-w-full h-full  rounded-lg ${
									isLoading ? "" : "border-b-1"
								}`}
							>
								<div
									className={` ${
										isPathnameInventory_Items
											? " "
											: "h-[calc(100%-72px)]"
									} ${
										darkMode
											? "grid max-w-full overflow-x-scroll"
											: "ut-inner px-0"
									}`}
								>
									<div className="relative h-full">
										<TableContent
											loadNextPage={loadNextPage}
											headerGroups={headerGroups}
											data={data}
											page={
												hasInfiniteScroll ? rows : page
											}
											getTableProps={getTableProps}
											getTableBodyProps={
												getTableBodyProps
											}
											prepareRow={prepareRow}
											activeIndex={activeIndex}
											handleRowClick={handleRowClick}
											handleClear={handleClear}
											setSearchColumn={setSearchColumn}
											showDetailsBox={showDetailsBox}
											setResetSearch={setResetSearch}
											editingHeader={editingHeader}
											searchText={searchText}
											setSearchText={setSearchText}
											setSubmittedSearchText={
												setSubmittedSearchText
											}
											setSortColumn={updateSortColumn}
											setEditingHeader={setEditingHeader}
											setHeaderValue={setHeaderValue}
											columnMappings={columnMappings}
											slug={slug}
											filterApplied={
												callFromFilter || false
											}
											onHeaderSortClick={
												handleHeaderSortClick
											}
											callFromFilter={callFromFilter}
											cellClickAction={callClickAction}
											pageTitle={pageTitle}
											viewSlug={viewSlug}
											selectedPurchaseOrderItemUuid={
												selectedPurchaseOrderItemUuid
											}
											isTableSearchAndSortEnabled={
												isTableSearchAndSortEnabled
											}
											hasTableRowExpansion={
												hasTableRowExpansion
											}
											tableDisplayData={tableDisplayData}
										/>
									</div>
								</div>

								{data.length !== 0 && !hasInfiniteScroll && (
									<div
										className={`${
											isNestedChildTable ? "pb-1" : ""
										} `}
									>
										{!isPathnameInventory_Items && (
											<TablePagination
												pageSize={pageSize}
												rowsPerPage={rowsPerPage}
												setPageSize={setPageSize}
												showSlideMenu={showDetailsBox}
												setCurrentPageInParent={
													setCurrentPage
												}
												currentPage={currentPage}
												setCurrentPageAndUpdateUrl={
													setCurrentPageAndUpdateUrl
												}
												totalRecordCount={totalRecords}
												slug={slug}
											/>
										)}
									</div>
								)}
							</div>
						</div>
					</div>
					{hasInfiniteScroll && (
						<div className="w-full py-2.5 border-t border-b border-navy-200  px-10 text-navy-500 text-base text-left ">
							<span>{data.length} items </span>
						</div>
					)}
					{shouldShowModal && modal}

					{approvalActionModal}
				</div>
			</motion.div>
		</AnimatePresence>
	);
};

export default PrimaryDataTable;
